import { HttpClient, HttpContext } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, catchError, forkJoin, from, map, of, switchMap } from 'rxjs';
import { API_BASE_URL } from '../../../common/tokens/api-base-url.token';
import { SKIP_ERROR_TOAST } from '../../../common/interceptors/skip-error-toast.token';
import { AuthSession } from '../../application/entities/auth-session.entity';
import { LoginCredentials } from '../../application/entities/login-credentials.entity';
import { PublicKeyResponse } from '../../application/entities/public-key-response.entity';
import { RecoverPasswordRequest } from '../../application/entities/recover-password-request.entity';
import { ResetPasswordRequest } from '../../application/entities/reset-password-request.entity';
import { AuthProviderPort } from '../../application/ports/auth-provider.port';
import { LoginResponseDto } from '../dto/login-response.dto';
import { RecoverPasswordResponseDto } from '../dto/recover-password-response.dto';
import { RecoverPasswordRequestDto } from '../dto/recover-password-request.dto';
import { ResetPasswordRequestDto } from '../dto/reset-password-request.dto';
import { toAuthSessionEntity, toLoginRequestDto } from '../mappers/auth.mapper';
import { encryptWithPublicKey } from '../crypto/rsa-encryptor';
import { StandardResponse } from '../../../common/dto/standar-response.dto';

const AUTH_CONTEXT = new HttpContext().set(SKIP_ERROR_TOAST, true);

@Injectable({ providedIn: 'root' })
export class AuthProviderService implements AuthProviderPort {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = inject(API_BASE_URL);

  login(credentials: LoginCredentials): Observable<AuthSession> {
    return this.getPublicKey().pipe(
      switchMap(({ publicKey }) =>
        from(encryptWithPublicKey(publicKey, credentials.password)),
      ),
      switchMap((encryptedPassword) =>
        this.http.post<LoginResponseDto>(`${this.baseUrl}auth/login`, {
          ...toLoginRequestDto(credentials),
          password: encryptedPassword,
        }, { context: AUTH_CONTEXT }),
      ),
      map(toAuthSessionEntity),
    );
  }

  getPublicKey(): Observable<PublicKeyResponse> {
    return this.http
      .get<StandardResponse<{ key: string; algorithm: string }>>(
        `${this.baseUrl}auth/public-key`,
        { context: AUTH_CONTEXT },
      )
      .pipe(map((r) => ({ publicKey: r.data!.key })));
  }

  logout(): Observable<void> {
    return this.http
      .get<StandardResponse<null>>(`${this.baseUrl}auth/logout`)
      .pipe(
        catchError(() => of(null)),
        map(() => void 0),
      );
  }

  recoverPassword(request: RecoverPasswordRequest): Observable<void> {
    const body: RecoverPasswordRequestDto = { email: request.email };
    return this.http
      .post<RecoverPasswordResponseDto>(`${this.baseUrl}auth/recover-password`, body, { context: AUTH_CONTEXT })
      .pipe(map(() => void 0));
  }

  resetPassword(request: ResetPasswordRequest): Observable<void> {
    return this.getPublicKey().pipe(
      switchMap(({ publicKey }) =>
        forkJoin({
          password: from(encryptWithPublicKey(publicKey, request.password)),
          confirmPassword: from(encryptWithPublicKey(publicKey, request.confirmPassword)),
        }),
      ),
      switchMap(({ password, confirmPassword }) => {
        const body: ResetPasswordRequestDto = {
          token: request.token,
          password,
          confirmPassword,
        };
        return this.http
          .post<StandardResponse<null>>(`${this.baseUrl}auth/reset-password`, body, { context: AUTH_CONTEXT })
          .pipe(map(() => void 0));
      }),
    );
  }
}

